/**
 * MDI Service
 * 
 * Service for interacting with MDI Telehealth integration endpoints.
 * Handles creating cases, checking status, and managing MDI settings.
 */

import { api, ApiResponse } from './api'

// Types
export interface MdiCase {
  id: string
  clinicId: string
  patientId: string
  questionnaireResponseId: string
  source: string
  program: string
  caseType?: string
  case_type?: string
  status: 'pending' | 'submitted' | 'in_review' | 'completed' | 'cancelled' | 'failed'
  mdiPatientId?: string
  mdiCaseId?: string
  encounterId?: string
  prescriptionId?: string
  prescriptionCount?: number
  completedAt?: string
  lastWebhookAt?: string
  createdAt: string
  updatedAt: string
  patient?: {
    id: string
    name: string
    email: string
  }
  encounter?: {
    id: string
    notes: string
    diagnosis: string
  }
}

export interface CreateCaseRequest {
  patientId: string
  questionnaireResponseId: string
  program: string
  source: string
  clinicId: string
}

export interface MdiSettings {
  enabled: boolean
  credentialProfile: string
  allowedPrograms: string[]
  webhookSecret?: string
}

export interface MdiCaseListParams {
  patientId?: string
  clinicId?: string
  status?: string
  page?: number
  limit?: number
}

// Service Implementation
export const mdiService = {
  /**
   * Create a new MDI case from a patient intake
   */
  async createCase(data: CreateCaseRequest): Promise<ApiResponse<MdiCase>> {
    // Transform camelCase to snake_case for backend API
    return api.post<ApiResponse<MdiCase>>('/mdi/cases', {
      patient_id: data.patientId,
      questionnaire_response_id: data.questionnaireResponseId,
      program: data.program,
      source: data.source,
      clinic_id: data.clinicId,
    })
  },

  /**
   * Get a specific MDI case by ID
   */
  async getCase(caseId: string): Promise<ApiResponse<MdiCase>> {
    return api.get<ApiResponse<MdiCase>>(`/mdi/cases/${caseId}`)
  },

  /**
   * List MDI cases with optional filtering
   */
  async listCases(params: MdiCaseListParams = {}): Promise<ApiResponse<MdiCase[]>> {
    const queryParams = new URLSearchParams()
    
    // Use snake_case for backend API
    if (params.patientId) queryParams.append('patient_id', params.patientId)
    if (params.clinicId) queryParams.append('clinic_id', params.clinicId)
    if (params.status) queryParams.append('status', params.status)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())

    const queryString = queryParams.toString()
    const url = queryString ? `/mdi/cases?${queryString}` : '/mdi/cases'
    
    // Backend returns { data: { cases: [...], pagination: {...} } }
    // Transform to { data: [...] } for frontend consumption
    const response = await api.get<ApiResponse<{ cases: MdiCase[]; pagination: unknown }>>(url)
    
    if (response.success && response.data?.cases) {
      return {
        success: true,
        data: response.data.cases.map(c => ({
          ...c,
          // Map snake_case to camelCase for frontend
          mdiCaseId: (c as unknown as Record<string, unknown>).mdi_case_id as string | undefined,
          caseType: (c as unknown as Record<string, unknown>).case_type as string | undefined,
          completedAt: (c as unknown as Record<string, unknown>).completed_at as string | undefined,
          createdAt: (c as unknown as Record<string, unknown>).created_at as string,
          updatedAt: (c as unknown as Record<string, unknown>).updated_at as string,
          prescriptionCount: (c as unknown as Record<string, unknown>).prescription_count as number | undefined,
        })),
      }
    }
    
    return { success: response.success, data: [] }
  },

  /**
   * Get MDI cases for a specific patient
   */
  async getPatientCases(patientId: string): Promise<ApiResponse<MdiCase[]>> {
    return this.listCases({ patientId })
  },

  /**
   * Get MDI settings for a specific clinic
   */
  async getSettings(clinicId?: string): Promise<ApiResponse<MdiSettings & { mdi_enabled?: boolean }>> {
    const params = clinicId ? `?clinic_id=${clinicId}` : ''
    return api.get<ApiResponse<MdiSettings & { mdi_enabled?: boolean }>>(`/mdi/settings${params}`)
  },

  /**
   * Update MDI settings for the current clinic (admin only)
   */
  async updateSettings(settings: Partial<MdiSettings>): Promise<ApiResponse<MdiSettings>> {
    return api.put<ApiResponse<MdiSettings>>('/mdi/settings', settings)
  },

  /**
   * Test MDI API connection
   */
  async testConnection(): Promise<ApiResponse<{ connected: boolean; message: string }>> {
    return api.post<ApiResponse<{ connected: boolean; message: string }>>('/mdi/test-connection')
  },

  /**
   * Check if MDI is enabled for a specific clinic
   */
  async isEnabled(clinicId?: string): Promise<boolean> {
    try {
      const response = await this.getSettings(clinicId)
      // Backend returns mdi_enabled, frontend uses enabled
      return response.success && (response.data?.mdi_enabled === true || response.data?.enabled === true)
    } catch (error) {
      console.error('Error checking MDI status:', error)
      return false
    }
  },

  /**
   * Get case status display info
   */
  getCaseStatusInfo(status: MdiCase['status']): { label: string; color: string; bgColor: string } {
    const statusMap: Record<MdiCase['status'], { label: string; color: string; bgColor: string }> = {
      pending: { label: 'Pending', color: 'text-amber-400', bgColor: 'bg-amber-500/10' },
      submitted: { label: 'Submitted', color: 'text-blue-400', bgColor: 'bg-blue-500/10' },
      in_review: { label: 'In Review', color: 'text-indigo-400', bgColor: 'bg-indigo-500/10' },
      completed: { label: 'Completed', color: 'text-green-400', bgColor: 'bg-green-500/10' },
      cancelled: { label: 'Cancelled', color: 'text-slate-400', bgColor: 'bg-slate-500/10' },
      failed: { label: 'Failed', color: 'text-red-400', bgColor: 'bg-red-500/10' },
    }
    return statusMap[status] || statusMap.pending
  },
}

export default mdiService







